import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { HighlightText } from '@/components/common/HighlightText';
import { SearchBar } from '@/components/common/SearchBar';
import { type NavItem, type NavSection } from '@/config/navigation';
import { useRole } from '@/hooks/useRole';
import { useCanOpen, useNavigation } from '@/hooks/useNavigation';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useSchoolScopedQuery } from '@/hooks/useSchoolScopedQuery';
import { listStudentsPage } from '@/api/students';
import { listStaffPage } from '@/api/staff';
import { queryKeys } from '@/lib/queryKeys';

interface ResultItem {
  id: string;
  group: 'Pages' | 'Students' | 'Staff';
  icon: React.ReactNode;
  label: string;
  secondary?: string;
  to: string;
}

function flattenPages(navigation: NavSection[]): ResultItem[] {
  const out: ResultItem[] = [];
  const visit = (item: NavItem, sectionLabel?: string) => {
    if (!item.children?.length) {
      out.push({ id: `page-${item.to}`, group: 'Pages', icon: item.icon ?? <ArrowForwardIcon fontSize="small" />, label: item.label, secondary: sectionLabel, to: item.to });
    } else {
      for (const child of item.children) {
        out.push({ id: `page-${child.to}`, group: 'Pages', icon: <ArrowForwardIcon fontSize="small" />, label: child.label, secondary: item.label, to: child.to });
      }
    }
  };
  for (const section of navigation) {
    for (const item of section.items) visit(item, section.label);
  }
  return out;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/** The page whose menu lets a role search students and staff here. */
const STUDENT_DIRECTORY_PATH = '/administrator/students';

/** Global Ctrl/Cmd+K search across the current role's pages, plus students and staff (roles that reach the student directory). */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { portal, roleCode } = useRole();
  const navigation = useNavigation(portal, roleCode);
  const pages = useMemo(() => flattenPages(navigation), [navigation]);
  // Student/staff results link into administrator pages: only a role whose menus reach the student directory gets them.
  const canSearchPeople = useCanOpen(STUDENT_DIRECTORY_PATH);

  // People are searched on the server, one small page per typed term, and only while the
  // palette is open: the shell keeps it mounted, so it must not load anything when closed.
  const term = useDebouncedValue(query.trim());
  const pageSize = term ? 8 : 4;
  const searchPeople = open && canSearchPeople;
  const { data: students = [] } = useSchoolScopedQuery(
    (schoolId) => [...queryKeys.students.all, 'palette', schoolId, term] as const,
    async (schoolId) => (await listStudentsPage(schoolId, { search: term || undefined, page: 1, pageSize })).items,
    undefined,
    { enabled: searchPeople, staleTime: 60_000, keepPrevious: true },
  );
  const { data: staff = [] } = useSchoolScopedQuery(
    (schoolId) => [...queryKeys.staff.listFor(schoolId), 'palette', term] as const,
    async (schoolId) => (await listStaffPage(schoolId, { search: term || undefined, page: 1, pageSize })).items,
    undefined,
    { enabled: searchPeople, staleTime: 60_000, keepPrevious: true },
  );

  // Reset search state whenever the dialog transitions to open, derived
  // during render rather than in an effect to avoid a cascading re-render.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }

  const results = useMemo<ResultItem[]>(() => {
    const q = query.trim().toLowerCase();

    // The server already matched the term; rows for an older term stay until the new ones arrive.
    const studentResults: ResultItem[] = (canSearchPeople ? students : [])
      .slice(0, q ? 8 : 4)
      .map((s) => ({
        id: `student-${s.studentId}`,
        group: 'Students',
        icon: <GroupsOutlinedIcon fontSize="small" />,
        label: s.studentName ?? 'Unnamed student',
        secondary: s.admissionNo ?? undefined,
        to: `/students/${s.studentId}/profile`,
      }));

    const staffResults: ResultItem[] = (canSearchPeople ? staff : [])
      .slice(0, q ? 8 : 4)
      .map((s) => ({
        id: `staff-${s.staff_Id}`,
        group: 'Staff',
        icon: <BadgeOutlinedIcon fontSize="small" />,
        label: s.staff_Name ?? 'Unnamed staff',
        to: '/administrator/staff/directory',
      }));

    const pageResults = q
      ? pages.filter((p) => p.label.toLowerCase().includes(q) || p.secondary?.toLowerCase().includes(q)).slice(0, 8)
      : pages.slice(0, 6);

    return [...pageResults, ...studentResults, ...staffResults];
  }, [query, pages, canSearchPeople, students, staff]);

  const grouped = useMemo(() => {
    const groups: Record<string, ResultItem[]> = {};
    for (const r of results) {
      (groups[r.group] ??= []).push(r);
    }
    return groups;
  }, [results]);

  const go = (item: ResultItem) => {
    void navigate(item.to);
    onClose();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = results[activeIndex];
      if (item) go(item);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  let runningIndex = -1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { position: 'fixed', top: 96, m: 0 } },
        // autoFocus alone loses to whatever held focus when Ctrl+K was pressed.
        transition: { onEntered: () => inputRef.current?.focus() },
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <SearchBar
          autoFocus
          inputRef={inputRef}
          placeholder="Search students, staff, or jump to a page…"
          value={query}
          onChange={(next) => {
            setQuery(next);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
        />
      </Box>
      <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
        {results.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No matches for "{query}".</Typography>
          </Box>
        ) : (
          <List ref={listRef} dense disablePadding>
            {(['Pages', 'Students', 'Staff'] as const).map((group) =>
              grouped[group]?.length ? (
                <Box key={group}>
                  <ListSubheader sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                    {group}
                  </ListSubheader>
                  {(grouped[group] ?? []).map((item) => {
                    runningIndex += 1;
                    // Captured per item: the handler below runs after the loop has finished counting.
                    const index = runningIndex;
                    const isActive = index === activeIndex;
                    return (
                      <ListItemButton
                        key={item.id}
                        selected={isActive}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => go(item)}
                      >
                        <ListItemIcon sx={{ minWidth: 34 }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={<HighlightText text={item.label} query={query} />}
                          secondary={item.secondary ? <HighlightText text={item.secondary} query={query} /> : undefined}
                        />
                      </ListItemButton>
                    );
                  })}
                </Box>
              ) : null,
            )}
          </List>
        )}
      </Box>
    </Dialog>
  );
}
